import React from 'react';
import {
  Shield,
  Footprints,
  BookOpen,
  Hammer,
  SquareLibrary,
  ArrowRight,
  ArrowUpRight,
  Users,
  Image,
  MapPin,
  Database,
  Globe,
  Search,
  AlertCircle,
  LucideIcon,
} from 'lucide-react';

interface HomeProps {
  onNavigate?: (page: string) => void;
}

interface Card {
  id: string;
  title: string;
  desc: string;
  icon: LucideIcon;
}

const primaryCards: Card[] = [
  { id: 'walkthrough', title: 'OSINT Walkthrough', desc: 'A full investigation, phase by phase - the fastest way to get going.', icon: Footprints },
  { id: 'contents', title: 'Contents', desc: 'Every discipline and resource, mapped out in one place.', icon: BookOpen },
  { id: 'tools', title: 'Tools & Techniques', desc: 'A curated directory of go-to OSINT resources by category.', icon: Hammer },
  { id: 'aggregation', title: 'Data Aggregation', desc: 'Turn raw findings into a structured AI summary with your own key.', icon: SquareLibrary },
];

const disciplines: { id: string; code: string; icon: LucideIcon }[] = [
  { id: 'socmint', code: 'SOCMINT', icon: Users },
  { id: 'imint', code: 'IMINT', icon: Image },
  { id: 'cybint', code: 'CYBINT', icon: Shield },
  { id: 'geoint', code: 'GEOINT', icon: MapPin },
  { id: 'finint', code: 'FININT', icon: Database },
  { id: 'darkint', code: 'DARKINT', icon: Globe },
  { id: 'weapint', code: 'WEAPINT', icon: Search },
];

const Home: React.FC<HomeProps> = ({ onNavigate }) => {
  const go = (id: string) => onNavigate && onNavigate(id);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 py-12 px-6">
      <div className="max-w-none">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-slate-700 light:bg-white bg-gradient-to-br from-slate-900 to-slate-800 p-10 mb-12">
          <div className="relative">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white">
                <Shield className="w-8 h-8" />
              </div>
              <div>
                <p className="text-xs font-bold tracking-widest text-orange-400 uppercase mb-1">
                  Open-Source Intelligence | Intelthread.dev
                </p>
                <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                  The OSINT Field Guide
                </h1>
              </div>
            </div>
            <p className="text-slate-300 max-w-2xl text-lg mb-8 leading-relaxed">
              A practical, hands-on guide to open-source investigation - from a single seed
              identifier to a documented report. Learn the disciplines, grab the tools, and
              follow a repeatable method.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => go('walkthrough')}
                className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold py-3 px-6 rounded-lg transition-all flex items-center gap-2"
              >
                <Footprints className="w-5 h-5" />
                Start the Walkthrough
              </button>
              <button
                onClick={() => go('contents')}
                className="bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center gap-2 border border-white/20"
              >
                Browse Contents
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-6">By Josh Holtz</p>
          </div>
        </div>

        {/* Primary cards */}
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-5">
          Jump in
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14">
          {primaryCards.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => go(c.id)}
                className="group relative text-left bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-orange-300 cursor-pointer"
              >
                <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-orange-500 to-amber-600 scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-300" />
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-900 dark:text-slate-100">{c.title}</h3>
                      <ArrowUpRight className="w-4 h-4 text-gray-300 dark:text-slate-500 group-hover:text-orange-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-slate-400 leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Disciplines */}
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-5">
          Intelligence disciplines
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-14">
          {disciplines.map((d) => {
            const Icon = d.icon;
            return (
              <button
                key={d.id}
                onClick={() => go(d.id)}
                className="group flex flex-col items-center gap-2 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 py-5 px-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-orange-300"
              >
                <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white transition-transform duration-300 group-hover:scale-110">
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold tracking-wider text-slate-700 dark:text-slate-300">
                  {d.code}
                </span>
              </button>
            );
          })}
        </div>

        {/* Ethics note */}
        <div className="border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-500/10 p-6 rounded-r-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-1">Use it responsibly</h3>
              <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
                Everything here is for authorized, lawful research and education. Stay within
                legal boundaries, respect privacy, protect your own operational security, and
                document your sources as you go.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
